/**
 * PoolAssetGrid
 *
 * Scrollable grid of asset pool thumbnails for selection.
 * Used in the Pool tab of AssetPickerModal.
 *
 * Built with Tailwind CSS v4.1.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { Check, Image as ImageIcon } from 'lucide-react';
import type { AssetPoolItem } from '@/lib/api/products';

// ============================================================================
// Types
// ============================================================================

interface PoolAssetGridProps {
  assets: AssetPoolItem[];
  selectedAsset: string | null;
  onSelectAsset: (id: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function PoolAssetGrid({ assets, selectedAsset, onSelectAsset }: PoolAssetGridProps) {
  if (assets.length === 0) {
    return <EmptyPoolState />;
  }

  return (
    <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto p-2">
      {assets.map((asset) => (
        <PoolAssetCard
          key={asset.id}
          asset={asset}
          isSelected={selectedAsset === asset.id}
          onSelect={() => onSelectAsset(asset.id)}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Single Asset Card
// ============================================================================

function PoolAssetCard({
  asset,
  isSelected,
  onSelect,
}: {
  asset: AssetPoolItem;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const borderClass = isSelected
    ? 'border-primary-500 shadow-md'
    : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500';

  return (
    <button
      className={`relative rounded-lg border-2 transition-all ${borderClass}`}
      onClick={onSelect}
    >
      <div className="aspect-square bg-neutral-100 dark:bg-neutral-800 rounded overflow-hidden">
        {asset.thumbnail_url ? (
          <img src={asset.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-6 w-6 text-neutral-400 dark:text-neutral-500" />
          </div>
        )}
      </div>
      <div className="p-1 text-center">
        <small className="text-xs text-neutral-600 dark:text-neutral-400">
          {asset.width && asset.height ? <>{asset.width}&times;{asset.height}</> : '—'}
        </small>
      </div>
      {isSelected && (
        <div className="absolute top-1 right-1 bg-primary-500 rounded-full p-1">
          <Check className="h-3 w-3 text-white" />
        </div>
      )}
    </button>
  );
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyPoolState() {
  return (
    <div className="text-center py-8 text-neutral-600 dark:text-neutral-400">
      <ImageIcon className="h-12 w-12 mx-auto mb-3 text-neutral-400 dark:text-neutral-500" />
      Keine Assets verfuegbar.
      <br />
      Synchronisieren Sie zuerst die Produktbilder.
    </div>
  );
}
